import { useMemo } from "react";

import { useFilter } from "@stadium/shared/src/hooks/use-filter";
import { scrollProductListingHeaderIntoView } from "@stadium/shared/src/helpers/product-listing-scroll";

import Button from "@stadium/shared/src/components/shared/button";

import { FilterType } from "@stadium/shared/src/components/filter/types";
import {
  shouldDisableClearAllForPreselect,
  isProtectedPreselectPill,
} from "@stadium/shared/src/components/filter/helpers";

import { ANALYTICS_EVENT_ACTION_REMOVED } from "@stadium/services/analytics/constants/default";
import { ANALYTICS_TARGET_FILTER } from "@constants/analytics/filter";
import { useShopAnalyticsContext } from "@hooks/analytics/use-shop-analytics-context";

import styles from "./main.module.scss";

const getPriceLabel = ({ prefix, suffix, value }) =>
  prefix ? `${prefix}${value}` : `${value}${suffix}`;

/** Recursively collects pills for selected leaf options. */
const collectSelectedOptions = (options, selectedValues, filterItem, filterId) => {
  if (!options) return [];

  return options.flatMap((opt) => {
    const optVal = String(opt.value ?? opt.id);
    const hasChildren = opt.children?.length > 0;
    const pills = [];

    if (selectedValues.includes(optVal) && !hasChildren) {
      const label = filterItem.label?.toLowerCase() === "size" ? `Size: ${opt.label}` : opt.label;
      pills.push({
        key: `${filterId}-${optVal}`,
        id: filterId,
        type: filterItem.type,
        value: optVal,
        label,
        preselectdValues: filterItem.preselectdValues,
      });
    }

    if (hasChildren) {
      pills.push(...collectSelectedOptions(opt.children, selectedValues, filterItem, filterId));
    }

    return pills;
  });
};

/** Builds a pill for a range filter (e.g. price). */
const buildRangePill = (filterItem, stateValue) => {
  if (stateValue?.min === undefined && stateValue?.max === undefined) return null;

  const prefix = filterItem.options?.prefix || "";
  const suffix = filterItem.options?.suffix || "";

  return {
    key: filterItem.id,
    id: filterItem.id,
    type: FilterType.RANGE,
    value: stateValue,
    label: `${getPriceLabel({ prefix, suffix, value: stateValue.min ?? 0 })} - ${getPriceLabel({
      prefix,
      suffix,
      value: stateValue.max ?? 0,
    })}`,
  };
};

const FilterPills = ({ config }) => {
  const {
    clear: clearFilters,
    remove: removeFilter,
    appliedFilters,
    appliedFiltersCount,
  } = useFilter(config);
  const shopAnalyticsContext = useShopAnalyticsContext();

  const pills = useMemo(() => {
    if (!config || !appliedFilters) return [];

    return Object.entries(appliedFilters).flatMap(([filterId, stateValue]) => {
      const filterItem = config.find((item) => item.id === filterId);
      if (!filterItem) return [];

      if (filterItem.type === FilterType.RANGE) {
        const pill = buildRangePill(filterItem, stateValue);
        return pill ? [pill] : [];
      }

      const values = Array.isArray(stateValue) ? stateValue : [stateValue];
      return collectSelectedOptions(filterItem.options, values, filterItem, filterId);
    });
  }, [config, appliedFilters]);

  const disableClearAllBtn = useMemo(
    () => shouldDisableClearAllForPreselect(appliedFilters || {}, config || []),
    [config, appliedFilters],
  );

  if (pills.length === 0) return null;

  return (
    <div className={styles.filterPillsContainer}>
      <div className={styles.pillsList}>
        {pills.map((pill) => (
          <div key={pill.key} className={styles.pillWrapper}>
            <span className={styles.pillItem}>{pill.label}</span>
            <Button
              type="button"
              styleType="no-style"
              className={styles.closeIconBtn}
              eventLocationAnalytics={{
                track: true,
                target: ANALYTICS_TARGET_FILTER,
                action: ANALYTICS_EVENT_ACTION_REMOVED,
                eventProperties: {
                  ...shopAnalyticsContext,
                  filter_type: pill.id,
                  filter_id: pill.type === FilterType.RANGE ? undefined : pill.value,
                  filter_value: false,
                  active_filters_count: pills.length - 1,
                },
              }}
              onClick={() => {
                removeFilter(pill);
                scrollProductListingHeaderIntoView();
              }}
              disabled={isProtectedPreselectPill(pill, appliedFiltersCount)}
              aria-label={`Remove filter ${pill.label}`}
            >
              &#x2715;
            </Button>
          </div>
        ))}
        <Button
          type="button"
          styleType="no-style"
          className={styles.clearAllBtn}
          disabled={disableClearAllBtn}
          onClick={() => {
            clearFilters();
            scrollProductListingHeaderIntoView();
          }}
        >
          CLEAR ALL
        </Button>
      </div>
    </div>
  );
};

export default FilterPills;
